import {
  CIActionGuard,
  CIRouteguard,
  CIScrollToTop,
  Header as CcsHeader,
  PageNotFound,
  setDateLocale,
  useUserPermission
} from 'ccs-ui-components';
import { Navigate } from 'react-router-dom';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AiAssistPage from './ai-assist/AiAssistPage';
import { requiredAuthPermission } from './common/constants/UserPermissionConstants';
import {
  buildAppTopNavItems,
  type AppHeaderUrlProps
} from './common/navigation/buildAppTopNavItems';
import './common/i18n/index';
import { RouteObject } from './common/types/interfaces';
import ControlPlaneWrapper from './control-plane';
import { routeObject as controlPlaneRoutes } from './control-plane/Routes';
import { toControlPlaneChildPath } from './control-plane/routePaths';
import JourneyBuilderWrapper from './journey-builder/modules/journey-builder-wrapper/JourneyBuilderWrapper';
import { journeyBuilderRoutes } from './journey-builder/constants/JdRoutes';

const AppHeader = CcsHeader;

const aiAssistPermissionKeys = [
  requiredAuthPermission.JD.view,
  requiredAuthPermission.JD.edit,
  requiredAuthPermission.JD.approve,
  requiredAuthPermission.Events.view,
  requiredAuthPermission.Events.edit,
  requiredAuthPermission.Events.approve,
  requiredAuthPermission.Services.view,
  requiredAuthPermission.Services.edit,
  requiredAuthPermission.Services.approve,
  requiredAuthPermission.CP.view,
  requiredAuthPermission.CP.edit
];

setDateLocale(process.env.REACT_APP_DATE_LOCALE ?? 'en-us');

const authOption = `AUTH_${process.env.REACT_APP_AUTH_OPTION}`;

const App = () => {
  const { permissionObject } = useUserPermission();
  const [showHeader, setShowHeader] = useState<boolean>(true);

  const { t: translate, i18n } = useTranslation();
  const languageChange = (code: string) => {
    i18n.changeLanguage(code);
    setDateLocale(code);
  };

  /** Must stay in sync with Header `urlProps`; drives links + permission flags. */
  const headerUrlProps = useMemo<AppHeaderUrlProps>(
    () => ({
      CP: {
        path: '/control-plane',
        permission: CIActionGuard(
          [
            requiredAuthPermission.Events.view,
            requiredAuthPermission.Events.edit,
            requiredAuthPermission.Events.approve,
            requiredAuthPermission.Services.view,
            requiredAuthPermission.Services.edit,
            requiredAuthPermission.Services.approve,
            requiredAuthPermission.CP.view,
            requiredAuthPermission.CP.edit
          ],
          permissionObject.permission
        )
      },

      JD: {
        path: '/journey-designer',
        permission: CIActionGuard(
          [
            requiredAuthPermission.JD.view,
            requiredAuthPermission.JD.edit,
            requiredAuthPermission.JD.approve
          ],
          permissionObject.permission
        )
      },

      OD: {
        path: `${process.env.REACT_APP_OBSERVABILITY_DASHBOARD_UI_URL}/dashboard`,
        permission: CIActionGuard(
          [requiredAuthPermission.OD.view],
          permissionObject.permission
        )
      },

      DP: {
        path: process.env.REACT_APP_DATA_PIPELINE_UI_URL,
        permission: false
      },

      ANE: {
        path: `${process.env.REACT_APP_ANE_UI_URL}/ane/configure-app`,
        permission: CIActionGuard(
          [requiredAuthPermission.ANE.edit, requiredAuthPermission.ANE.view],
          permissionObject.permission
        )
      },

      UM: {
        path: `${process.env.REACT_APP_UM_UI_URL}/dashboard/administration`,
        permission:
          CIActionGuard(
            [
              requiredAuthPermission.Admin.view,
              requiredAuthPermission.Admin.edit,
              requiredAuthPermission.Maintainence.view,
              requiredAuthPermission.Admin.edit
            ],
            permissionObject.permission
          ) && authOption.substring(5) === 'auth0'
      }
    }),
    [permissionObject.permission]
  );

  /** Custom top nav items (Header → TopNavigation). */
  const topNavItems = useMemo(
    () => buildAppTopNavItems(translate, headerUrlProps),
    [translate, headerUrlProps, i18n.language]
  );

  const showHeaderHandler = (val: boolean) => {
    setShowHeader(val);
  };

  const logOutHandler = () => {
    const path = window.location.pathname;
    let redirectUri = window.location.origin;
    if (path.includes('journey-designer')) {
      redirectUri = `${window.location.origin}/journey-designer`;
    } else if (path.includes('cipaidesigner')) {
      redirectUri = `${window.location.origin}/cipaidesigner`;
    }
    permissionObject.logoutHandler(redirectUri);
  };

  const controlDash = <ControlPlaneWrapper />;

  const controlPlanePermissions = [
    requiredAuthPermission.Events.view,
    requiredAuthPermission.Events.edit,
    requiredAuthPermission.Events.approve,
    requiredAuthPermission.Services.view,
    requiredAuthPermission.Services.edit,
    requiredAuthPermission.Services.approve,
    requiredAuthPermission.CP.view,
    requiredAuthPermission.CP.edit
  ];

  const routes: RouteObject[] = [
    {
      path: '/control-plane',
      element: controlDash,
      requiredPermissions: controlPlanePermissions,
      children: controlPlaneRoutes.map((route) => ({
        ...route,
        path: toControlPlaneChildPath(route.path)
      }))
    },
    {
      path: `${journeyBuilderRoutes.parentRoute}/*`,
      element: <JourneyBuilderWrapper showHeaderHandler={showHeaderHandler} />,
      requiredPermissions: [
        requiredAuthPermission.JD.view,
        requiredAuthPermission.JD.edit,
        requiredAuthPermission.JD.approve
      ]
    },
    {
      path: '/ai-assist/*',
      element: <Navigate to="/cipaidesigner" replace />,
      requiredPermissions: aiAssistPermissionKeys
    },
    {
      path: '/cipaidesigner/*',
      element: <AiAssistPage />,
      requiredPermissions: aiAssistPermissionKeys
    },
    {
      path: '/not-found',
      element: <PageNotFound />,
      requiredPermissions: [
        requiredAuthPermission.Events.view,
        requiredAuthPermission.Events.edit,
        requiredAuthPermission.Events.approve,
        requiredAuthPermission.Services.view,
        requiredAuthPermission.Services.edit,
        requiredAuthPermission.Services.approve,
        requiredAuthPermission.CP.view,
        requiredAuthPermission.CP.edit,
        requiredAuthPermission.JD.view,
        requiredAuthPermission.JD.edit,
        requiredAuthPermission.JD.approve
      ]
    },
    {
      path: '/',
      element: <Navigate to="/control-plane/dashboard" />,
      requiredPermissions: [
        requiredAuthPermission.Events.view,
        requiredAuthPermission.Events.edit,
        requiredAuthPermission.Events.approve,
        requiredAuthPermission.Services.view,
        requiredAuthPermission.Services.edit,
        requiredAuthPermission.Services.approve,
        requiredAuthPermission.CP.view,
        requiredAuthPermission.CP.edit,
        requiredAuthPermission.JD.view,
        requiredAuthPermission.JD.edit,
        requiredAuthPermission.JD.approve
      ]
    }
  ];

  return (
    <div className="app">
      {showHeader && (
        <AppHeader
          urlProps={headerUrlProps}
          topNavItems={topNavItems}
          logOutHandler={logOutHandler}
          languageChange={languageChange}
          userName={permissionObject.user.name}
          userEmail={permissionObject.user.email}
        />
      )}
      <CIRouteguard
        routes={routes}
        userPermissions={permissionObject.permission}
      />
      <CIScrollToTop />
    </div>
  );
};

export default App;
